import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import httpClient from '../api/httpClient';
import { onboardingApi } from '../api/onboardingApi';
import { vibeService } from '../services/VibeService';
import OnboardingLogoutButton from '../components/auth/OnboardingLogoutButton';
import './OnboardingPreferences.css';

type PreferenceStep = 1 | 2;

interface AspirationOption {
  domain_id: string;
  career_role: string;
}

const GOAL_OPTIONS = [
  { id: 'prepare_for_interview', label: 'Prepare for interview' },
  { id: 'get_placed', label: 'Get placed' },
];

const MAX_ASPIRATIONS = 3;

const OnboardingPreferences: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<PreferenceStep>(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedGoal, setSelectedGoal] = useState<string | null>(null);
  const [selectedAspirations, setSelectedAspirations] = useState<string[]>([]);
  const [aspirationOptions, setAspirationOptions] = useState<AspirationOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [resumeUploaded, setResumeUploaded] = useState(false);
  const [resumeFilename, setResumeFilename] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prefResponse, profileResponse] = await Promise.all([
          httpClient.get('orchestrator/preferences/list_preference'),
          onboardingApi.getUserProfile(),
        ]);
        setAspirationOptions(prefResponse.data);
        setUserId(profileResponse.data.user_id);
        if (profileResponse.data.onboarding?.steps?.['1_resume_upload']) {
          setResumeUploaded(true);
          setResumeFilename(profileResponse.data.resume_summary?.original_filename || 'resume.pdf');
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setIsLoadingOptions(false);
      }
    };
    fetchData();
  }, []);

  const isSkipped = selectedAspirations.includes('no_preference');

  const handleToggleAspiration = (id: string) => {
    setSelectedAspirations((prev) => {
      // Toggling no_preference: exclusive — clears everything else
      if (id === 'no_preference') {
        return prev.includes('no_preference') ? [] : ['no_preference'];
      }
      // If no_preference was selected, clear it and select this one
      if (prev.includes('no_preference')) {
        return [id];
      }
      if (prev.includes(id)) {
        return prev.filter((a) => a !== id);
      }
      if (prev.length >= MAX_ASPIRATIONS) return prev;
      return [...prev, id];
    });
  };

  const handleContinueToStep2 = () => {
    if (!selectedGoal || !firstName.trim() || !lastName.trim()) return;
    setStep(2);
  };

  const handleBackToStep1 = () => {
    setStep(1);
  };

  const handleFinish = async () => {
    if (!selectedGoal || selectedAspirations.length === 0) return;

    setIsSaving(true);
    setError(null);

    try {
      const jobTypes = isSkipped ? ['nopref_01'] : selectedAspirations;

      await httpClient.post('orchestrator/preferences/setup', {
        user_id: userId,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        job_types: jobTypes,
        experience_years: 0,
        target_companies: [],
        preferred_languages: [],
      });

      localStorage.setItem('userPreferences', JSON.stringify({
        goal: selectedGoal,
        aspirations: selectedAspirations,
      }));
      localStorage.setItem('preferencesCompleted', 'true');
      vibeService.trigger('success');
      if (resumeUploaded) {
        navigate('/interview', { state: { isOnboarding: true } });
      } else {
        navigate('/onboarding/resume');
      }
    } catch (err: any) {
      console.error('Failed to save preferences:', err);
      setError('Failed to save preferences. Please try again.');
      vibeService.trigger('error');
      setIsSaving(false);
    }
  };

  return (
    <div className="onboarding-preferences">
      <OnboardingLogoutButton />
      <div className="pref-split-container">
        {/* Left Side - Welcome Panel */}
        <div className="pref-left">
          <div className="pref-welcome-content">
            <div className="pref-logo">
              <img src="/image_i_do1.png" alt="Luci logo" className="luci-logo" />
            </div>
            <h1 className="pref-welcome-title">You're new here!</h1>
            <p className="pref-welcome-subtitle">
              Tell us a bit about yourself so we can personalize your experience.
            </p>
            <p className="pref-welcome-description">
              Luci provides fully personalized career preparation through AI-driven interviews, tracking your progress continuously to help you improve.
            </p>

            {/* Step Indicator */}
            <div className="pref-step-indicator-vertical">
              <div className="pref-step-v active">
                <div className="pref-step-dot-v" />
                <span>Preferences</span>
              </div>
              <div className="pref-step-line-v" />
              <div className="pref-step-v">
                <div className="pref-step-dot-v" />
                <span>Upload Resume</span>
              </div>
              <div className="pref-step-line-v" />
              <div className="pref-step-v">
                <div className="pref-step-dot-v" />
                <span>AI Interview</span>
              </div>
            </div>


          </div>
        </div>

        {/* Right Side - Question Card */}
        <div className="pref-right">
          {/* Step 1: Goal */}
          {step === 1 && (
            <div className="pref-card" key="step-1">
              <div className="pref-card-header">
                <div className="pref-step-badge">Step 1 of 2</div>
                <h1>Tell us about yourself</h1>
                <p>Your name and goal help us personalize your experience.</p>
              </div>

              <div className="pref-name-row">
                <div className="pref-input-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    id="firstName"
                    type="text"
                    className="pref-text-input"
                    placeholder="e.g. Ravi"
                    maxLength={20}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="pref-input-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    id="lastName"
                    type="text"
                    className="pref-text-input"
                    placeholder="e.g. Kumar"
                    maxLength={20}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="pref-goal-section">
                <h3 className="pref-section-label">What is your goal?</h3>
                <div className="pref-options-list">
                  {GOAL_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      className={`pref-option-card ${selectedGoal === option.id ? 'selected' : ''}`}
                      onClick={() => setSelectedGoal(option.id)}
                    >
                      <div className="pref-option-radio" />
                      <span className="pref-option-label">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pref-button-row">
                <button
                  className="pref-continue-button"
                  disabled={!selectedGoal || !firstName.trim() || !lastName.trim()}
                  onClick={handleContinueToStep2}
                >
                  <span>Continue</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Aspiration */}
          {step === 2 && (
            <div className="pref-card" key="step-2">
              <div className="pref-card-header">
                <div className="pref-step-badge">Step 2 of 2</div>
                <h1>What do you aspire to become?</h1>
                <p>Select up to {MAX_ASPIRATIONS} roles that match your career aspirations.</p>
              </div>

              {!isSkipped && selectedAspirations.length > 0 && (
                <div className="pref-selection-count">
                  {selectedAspirations.length} of {MAX_ASPIRATIONS} selected
                </div>
              )}

              {isLoadingOptions ? (
                <div className="pref-loading-options">
                  <div className="pref-loading-spinner" />
                  <p>Loading options...</p>
                </div>
              ) : (
                <div className="pref-options-grid">
                  {aspirationOptions
                    .filter((option) => option.domain_id.toLowerCase() !== 'no_preference' && option.career_role.toLowerCase() !== 'no_preference')
                    .map((option) => {
                      const isSelected = selectedAspirations.includes(option.domain_id);
                      const isDisabled = !isSelected && !isSkipped && selectedAspirations.length >= MAX_ASPIRATIONS;
                      return (
                        <button
                          key={option.domain_id}
                          className={`pref-option-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${isSkipped ? 'disabled' : ''}`}
                          onClick={() => handleToggleAspiration(option.domain_id)}
                          disabled={isDisabled || isSkipped}
                        >
                          <div className="pref-option-checkbox" />
                          <span className="pref-option-label">{option.career_role}</span>
                        </button>
                      );
                    })}

                  <button
                    className={`pref-skip-card ${isSkipped ? 'selected' : ''}`}
                    onClick={() => handleToggleAspiration('no_preference')}
                  >
                    Skip for now
                  </button>
                </div>
              )}

              <div className="pref-button-row">
                <button
                  className="pref-back-button"
                  onClick={handleBackToStep1}
                  disabled={isSaving}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  <span>Back</span>
                </button>
                <button
                  className="pref-continue-button"
                  disabled={selectedAspirations.length === 0 || isSaving}
                  onClick={handleFinish}
                >
                  {isSaving ? (
                    <div className="pref-saving-spinner" />
                  ) : (
                    <>
                      <span>Continue</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </>
                  )}
                </button>
              </div>

              {error && <p className="pref-error-message">{error}</p>}

              {resumeUploaded && (
                <div className="pref-resume-ready">
                  <div className="pref-resume-file">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span>{resumeFilename}</span>
                  </div>
                  <button
                    className="pref-begin-interview-button"
                    onClick={() => navigate('/interview', { state: { isOnboarding: true } })}
                  >
                    Begin Interview
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPreferences;
